import {
  BasePaginationQueryParams,
  BasePaginationResponse,
  DbSchema,
  Nullable,
  PaginationInfoSchema,
  PaginationSchema,
  paramByIDSchema,
  paramOrderProductHighlight,
  Product,
  ProductAllowedPaymentMethod,
  ProductDivisionFilterSchema,
  ProductDivisionQuery,
  ReadManyProductSchema,
  RecursiveStatic,
  SearchSchema,
  SortFilterSchema,
  StorageFilterSchema,
  WarehouseIdCategorySchema,
  WarehouseIdSchema,
} from '@diamond/shared/schema';
import {
  BundledDiscountPromoSchema,
  BundledFreeGoodPromoSchema,
  SingleDiscountPromoSchema,
  SingleFreeGoodPromoSchema,
} from '@diamond/shared/schema/promo';
import { Type } from '@sinclair/typebox';

const ProductStockStatus = {
  AVAILABLE: 'AVAILABLE',
  LIMITED_STOCK: 'LIMITED_STOCK',
  OUT_OF_STOCK: 'OUT_OF_STOCK',
} as const;
export type ProductStockStatus =
  (typeof ProductStockStatus)[keyof typeof ProductStockStatus];
export const ProductStockStatusSchema = Type.Enum(ProductStockStatus);

export const ProductPaymentMethodResponseFieldsSchema = Type.Object({
  allowed_payment_methods: Nullable(Type.Array(ProductAllowedPaymentMethod)),
  requires_weight_adjustment: Nullable(Type.Boolean()),
});
export type ProductPaymentMethodResponseFields = RecursiveStatic<
  typeof ProductPaymentMethodResponseFieldsSchema
>;

export const ProductResponseSchema = Type.Intersect([
  Type.Omit(Product, [
    'category_id',
    'sub_category_id',
    'third_sub_category_id',
    'created_at',
    'updated_at',
    'deleted_at',
    'created_by',
    'updated_by',
    'deleted_by',
    'material_division',
    'can_overwrite_price',
    'category_2',
    'transportation_group',
    'flag_fs',
    'flag_gt',
    'flag_sts',
    'flag_show_pepperi',
    'is_valid_data',
    'finance_category_5',
    'finance_category_6',
    'finance_category_7',
    'suggestion_1',
    'suggestion_2',
    'tag',
    'is_only_for_sukanda_partner',
    'is_only_for_online_business',
    'is_eligible_for_so_automation',
    'conversion_qty',
    'conversion_unit',
    'conversion_sales_unit',
  ]),
  Type.Object({
    stock: Type.Integer(),
    stock_status: ProductStockStatusSchema,
    category_name: Type.String(),
    wishlist_id: Nullable(Type.String({ format: 'uuid' })),
    sub_category_name: Type.Optional(Type.String()),
    sub_third_category_name: Type.Optional(Type.String()),
    product_images: Type.Array(Type.String()),
    product_image_thumbnails: Type.Array(Type.String()),
    product_images_medium: Type.Array(Type.String()),
    product_images_large: Type.Array(Type.String()),
    prices: Type.Array(
      Type.Object({
        price: Type.Number(),
        min_order_quantity: Type.Integer(),
      })
    ),
    stock_magnifier: Type.Integer(),
  }),
]);

export const ProductWithPromoResponseSchema = Type.Intersect([
  ProductResponseSchema,
  Type.Object({
    free_good_promos: Type.Array(SingleFreeGoodPromoSchema),
    discount_promos: Type.Array(SingleDiscountPromoSchema),
    bundled_discount_promos: Type.Array(BundledDiscountPromoSchema),
    bundled_free_good_promos: Type.Array(BundledFreeGoodPromoSchema),
    count_voucher: Type.Optional(Type.Integer({ minimum: 0 })),
  }),
]);

const HighlightProductWithVoucherCountSchema = Type.Intersect([
  ProductWithPromoResponseSchema,
  Type.Object({
    count_voucher: Type.Optional(Type.Integer({ minimum: 0 })),
  }),
]);

const ProductDetailBaseResponseSchema = Type.Intersect([
  ProductWithPromoResponseSchema,
  Type.Object({
    count_recipes: Type.Integer({ minimum: 0 }),
  }),
]);

export const AuthenticatedProductDetailResponseSchema = Type.Intersect([
  ProductDetailBaseResponseSchema,
  ProductPaymentMethodResponseFieldsSchema,
]);

export const ProductDetailResponseSchema = Type.Intersect([
  ProductDetailBaseResponseSchema,
  Type.Partial(ProductPaymentMethodResponseFieldsSchema),
]);

export const ProductExcludeBranchRuleSchema = Type.Object(
  {
    product_item_code: Type.Integer({ minimum: 1 }),
    distribution_channel: Type.String({ minLength: 1, pattern: '\\S' }),
    plant: Type.Integer({ minimum: 1 }),
    cg_1_business_segmentation: Type.Optional(Nullable(Type.String())),
  },
  { additionalProperties: false }
);

const ProductExcludeBranchItemSchema = Type.Object(
  {
    id: Type.String({ format: 'uuid' }),
    product_item_code: Type.Integer({ minimum: 1 }),
    distribution_channel: Type.String(),
    plant: Type.Integer({ minimum: 1 }),
    cg_1_business_segmentation: Nullable(Type.String()),
    created_at: Type.String({ format: 'date-time' }),
    updated_at: Type.String({ format: 'date-time' }),
  },
  { additionalProperties: false }
);

const ProductExcludeBranchListQuerySchema = Type.Intersect([
  BasePaginationQueryParams,
  Type.Object(
    {
      product_item_code: Type.Optional(Type.Integer({ minimum: 1 })),
      distribution_channel: Type.Optional(Type.String({ minLength: 1 })),
      plant: Type.Optional(Type.Integer({ minimum: 1 })),
      cg_1_business_segmentation: Type.Optional(Type.String({ minLength: 1 })),
    },
    { additionalProperties: false }
  ),
]);

const ProductExcludeBranchMutationCountSchema = Type.Object({
  created: Type.Integer({ minimum: 0 }),
  unchanged: Type.Integer({ minimum: 0 }),
});

export const ProductExcludeBranchCrudSchema = {
  path: '/product-exclude-branch',
  list: {
    path: '/',
    query: ProductExcludeBranchListQuerySchema,
    response: BasePaginationResponse(ProductExcludeBranchItemSchema),
  },
  create: {
    path: '/',
    body: ProductExcludeBranchRuleSchema,
    response: Type.Object({ data: ProductExcludeBranchItemSchema }),
  },
  bulkCreate: {
    path: '/bulk',
    body: Type.Object(
      {
        items: Type.Array(ProductExcludeBranchRuleSchema, {
          minItems: 1,
          maxItems: 1000,
        }),
      },
      { additionalProperties: false }
    ),
    response: Type.Object({
      data: ProductExcludeBranchMutationCountSchema,
    }),
  },
  delete: {
    path: '/:id',
    params: Type.Object({
      id: Type.String({ format: 'uuid' }),
    }),
    response: Type.Object({
      data: Type.Object({
        id: Type.String({ format: 'uuid' }),
        status: Type.Literal('deleted'),
      }),
    }),
  },
  bulkDelete: {
    path: '/bulk-delete',
    body: Type.Object(
      {
        ids: Type.Array(Type.String({ format: 'uuid' }), {
          minItems: 1,
          maxItems: 1000,
        }),
      },
      { additionalProperties: false }
    ),
    response: Type.Object({
      data: Type.Object({
        deleted: Type.Integer({ minimum: 0 }),
        unchanged: Type.Integer({ minimum: 0 }),
      }),
    }),
  },
};

export type ProductExcludeBranchCrud = RecursiveStatic<
  typeof ProductExcludeBranchCrudSchema
>;

export const ProductCrudSchema = {
  path: '/product',
  getMany: {
    path: '',
    query: ReadManyProductSchema,
    response: Type.Object({
      data: Type.Array(ProductWithPromoResponseSchema),
      pagination: PaginationInfoSchema,
    }),
  },
  getProductRecommendations: {
    path: '/recommendations',
    query: Type.Object({
      search_query: Type.String(),
      warehouse: Type.String(),
    }),
    response: Type.Object({
      products: Type.Array(Type.String()),
      brands: Type.Array(Type.String()),
    }),
  },
  getProductSubstitutions: {
    path: '/:id/substitutions',
    params: paramByIDSchema,
    query: Type.Omit(ReadManyProductSchema, ['search_query']),
    response: Type.Object({
      data: Type.Array(ProductWithPromoResponseSchema),
      pagination: PaginationInfoSchema,
    }),
  },
  getById: {
    path: '/:id',
    query: Type.Object({
      warehouse: Type.Optional(Type.String({ format: 'uuid', minLength: 36 })),
      is_public: Type.Optional(Type.Boolean()),
    }),
    response: ProductDetailResponseSchema,
  },
  getManyCategory: {
    path: '/category',
    query: Type.Intersect([
      PaginationSchema,
      Type.Pick(SearchSchema, ['search_query']),
      ProductDivisionQuery,
      WarehouseIdCategorySchema,
      Type.Object({
        storage_code: Type.Optional(
          Type.Union([
            Type.Literal('dry'),
            Type.Literal('frozen'),
            Type.Literal('dry_ac'),
            Type.Literal('chilled'),
          ])
        ),
        product_id_substitute: Type.Optional(Type.String({ format: 'uuid' })),
      }),
    ]),
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Omit(DbSchema['product_categories'], [
            'updated_at',
            'deleted_at',
            'created_by',
            'updated_by',
            'deleted_by',
          ]),
          Type.Object({
            product_count: Type.Number(),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  getSubCategoryById: {
    path: '/sub-category/:id',
    query: Type.Intersect([
      Type.Pick(SearchSchema, ['search_query']),
      ProductDivisionQuery,
      WarehouseIdCategorySchema,
      Type.Object({
        storage_code: Type.Optional(
          Type.Union([
            Type.Literal('dry'),
            Type.Literal('frozen'),
            Type.Literal('dry_ac'),
            Type.Literal('chilled'),
          ])
        ),
        product_id_substitute: Type.Optional(Type.String({ format: 'uuid' })),
      }),
    ]),
    response: Type.Object({
      data: Type.Array(
        Type.Object({
          category_lv2: Type.String({ format: 'uuid' }),
          name: Type.String(),
          product_count: Type.Number(),
          third_category: Type.Array(
            Type.Object({
              category_lv3: Type.String({ format: 'uuid' }),
              name: Type.String(),
              product_count: Type.Number(),
            })
          ),
        })
      ),
    }),
  },
  getProductFilter: {
    path: '/filters',
    query: Type.Object({
      is_public: Type.Optional(Type.Boolean()),
    }),
    response: Type.Object({
      divisions: ProductDivisionFilterSchema,
      storages: StorageFilterSchema,
      sorts: SortFilterSchema,
    }),
  },

  // ------------------------------------------------------
  // User Highlight ( /product/highlight )
  // ------------------------------------------------------
  readHighlight: {
    path: '/highlight',
    query: WarehouseIdSchema,
    response: Type.Array(
      Type.Intersect([
        Type.Omit(DbSchema['product_highlights'], [
          'created_at',
          'updated_at',
          'deleted_at',
          'created_by',
          'updated_by',
          'deleted_by',
        ]),
        Type.Object({
          products: Type.Array(ProductWithPromoResponseSchema),
        }),
      ])
    ),
  },
  readHighlights: {
    path: '/highlights',
    query: WarehouseIdSchema,
    response: Type.Array(
      Type.Omit(DbSchema['product_highlights'], [
        'created_at',
        'updated_at',
        'deleted_at',
        'created_by',
        'updated_by',
        'deleted_by',
      ])
    ),
  },
  readOneHighlight: {
    path: '/highlight/:id',
    query: WarehouseIdSchema,
    params: paramByIDSchema,
    response: DbSchema['product_highlights'],
  },
  getManyProductFromHighlight: {
    path: '/highlight/:id/item',
    params: paramByIDSchema,
    query: Type.Intersect([PaginationSchema, WarehouseIdSchema]),
    response: Type.Object({
      data: Type.Array(HighlightProductWithVoucherCountSchema),
      pagination: PaginationInfoSchema,
    }),
  },

  // ------------------------------------------------------
  // Admin Highlight ( backoffice/product/highlight )
  // ------------------------------------------------------
  createHighlight: {
    path: '/highlight',
    body: Type.Intersect([
      Type.Omit(DbSchema['product_highlights'], [
        'id',
        'created_at',
        'updated_at',
        'deleted_at',
        'created_by',
        'updated_by',
        'deleted_by',
      ]),
      Type.Object({
        products: Type.Optional(
          Type.Array(Type.String({ format: 'uuid' }), { minItems: 1 })
        ),
        type: Type.Optional(
          Type.Union([Type.Literal('general'), Type.Literal('specific')])
        ),
        business_types: Type.Optional(
          Type.Array(
            Type.Object({
              cg_3_business_type: Type.String({ minLength: 1 }),
              cg_3_business_type_desc: Type.String({ minLength: 1 }),
            }),
            { minItems: 1 }
          )
        ),
        shiptos: Type.Optional(Type.Any()), // File upload will be handled separately
      }),
    ]),
    response: DbSchema['product_highlights'],
  },
  getManyHighlight: {
    path: '/highlight',
    response: Type.Array(
      Type.Intersect([
        Type.Omit(DbSchema['product_highlights'], [
          'created_at',
          'updated_at',
          'deleted_at',
          'created_by',
          'updated_by',
          'deleted_by',
        ]),
        Type.Object({
          images: Type.Array(Type.String({ format: 'uri' })),
          image_thumbnails: Type.Array(Type.String()),
          total_shiptos: Type.Integer({ minimum: 0 }),
          total_business_types: Type.Integer({ minimum: 0 }),
        }),
      ])
    ),
  },
  getManyHighlights: {
    path: '/highlights',
    query: Type.Intersect([
      PaginationSchema,
      Type.Object({
        search_query: Type.Optional(Type.String()),
        business_type: Type.Optional(Type.Array(Type.String())),
        business_type_desc: Type.Optional(Type.Array(Type.String())),
        business_segmentation: Type.Optional(Type.Array(Type.String())),
        distribution_channel: Type.Optional(Type.Array(Type.String())),
        sap_no_shipto: Type.Optional(Type.Array(Type.String())),
      }),
    ]),
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Omit(DbSchema['product_highlights'], [
            'created_at',
            'updated_at',
            'deleted_at',
            'created_by',
            'updated_by',
            'deleted_by',
          ]),
          Type.Object({
            images: Type.Array(Type.String({ format: 'uri' })),
            image_thumbnails: Type.Array(Type.String()),
            total_shiptos: Type.Integer({ minimum: 0 }),
            total_business_types: Type.Integer({ minimum: 0 }),
            total_products: Type.Integer({ minimum: 0 }),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  getOneHighlight: {
    path: '/highlight/:id',
    params: paramByIDSchema,
    response: Type.Intersect([
      DbSchema['product_highlights'],
      Type.Object({
        total_shiptos: Type.Optional(Type.Integer({ minimum: 0 })),
        business_types: Type.Optional(
          Type.Array(
            Type.Object({
              cg_3_business_type: Type.String(),
              cg_3_business_type_desc: Type.String(),
            })
          )
        ),
      }),
    ]),
  },
  getManyProductFromHighlightAdmin: {
    path: '/highlight/:id/item',
    params: paramByIDSchema,
    query: PaginationSchema,
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Pick(Product, ['id', 'title']),
          Type.Object({
            index: Type.Number(),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  reorderHighlight: {
    path: `/highlight/:id/reorder`,
    params: paramByIDSchema,
    body: Type.Pick(DbSchema['product_highlights'], ['index']),
    response: Type.Array(DbSchema['product_highlights']),
  },
  updateHighlight: {
    path: '/highlight/:id',
    params: paramByIDSchema,
    body: Type.Intersect([
      Type.Pick(DbSchema['product_highlights'], [
        'title',
        'business_segmentation',
        'distribution_channel',
      ]),
      Type.Object({
        products: Type.Optional(
          Type.Array(Type.String({ format: 'uuid' }), { minItems: 1 })
        ),
        type: Type.Optional(
          Type.Union([Type.Literal('general'), Type.Literal('specific')])
        ),
        business_types: Type.Optional(
          Type.Array(
            Type.Object({
              cg_3_business_type: Type.String({ minLength: 1 }),
              cg_3_business_type_desc: Type.String({ minLength: 1 }),
            }),
            { minItems: 1 }
          )
        ),
        shiptos: Type.Optional(Type.Any()), // File upload will be handled separately
      }),
    ]),
    response: DbSchema['product_highlights'],
  },
  deleteHighlight: {
    path: '/highlight/:id',
    params: paramByIDSchema,
    response: Type.Object({ message: Type.String() }),
  },
  addProductToHighlight: {
    path: '/highlight/:id/item',
    params: paramByIDSchema,
    body: Type.Object({
      products: Type.Array(Type.String({ format: 'uuid' }), { minItems: 1 }),
    }),
    response: Type.Object({ message: Type.String() }),
  },
  deleteProductFromHighlight: {
    path: '/highlight/:id/item',
    params: paramByIDSchema,
    body: Type.Object({
      products: Type.Array(Type.String({ format: 'uuid' })),
    }),
    response: Type.Object({
      message: Type.String(),
    }),
  },

  // ------------------------------------------------------
  // Keyword ( /product/keyword )
  // ------------------------------------------------------
  addProductKeyword: {
    path: '/keyword',
    body: Type.Pick(DbSchema['product_keywords'], [
      'name',
      'url',
      'index',
      'business_segmentation',
      'distribution_channel',
    ]),
    response: DbSchema['product_keywords'],
  },
  reorderProductKeyword: {
    path: '/keyword/:id/reorder',
    body: Type.Pick(DbSchema['product_keywords'], ['index']),
    response: Type.Array(DbSchema['product_keywords']),
  },
  getManyProductKeyword: {
    path: '/keyword',
    response: Type.Array(DbSchema['product_keywords']),
  },
  buyerGetManyProductKeyword: {
    path: '/keyword',
    query: Type.Object(
      {
        warehouse: Type.Optional(
          Type.String({ format: 'uuid', minLength: 36 })
        ),
        shipto_id: Type.Optional(
          Type.String({ format: 'uuid', minLength: 36 })
        ),
      },
      { minProperties: 1 }
    ),
    response: Type.Array(DbSchema['product_keywords']),
  },
  getProductKeywordById: {
    path: '/keyword/:id',
    response: DbSchema['product_keywords'],
  },
  updateProductKeyword: {
    path: '/keyword/:id',
    body: Type.Pick(DbSchema['product_keywords'], [
      'name',
      'url',
      'business_segmentation',
      'distribution_channel',
    ]),
    response: DbSchema['product_keywords'],
  },
  deleteProductKeyword: {
    path: '/keyword/:id',
    response: Type.Object({
      message: Type.String(),
    }),
  },

  // ------------------------------------------------------
  // Valuation Type
  // ------------------------------------------------------
  getManyValuationType: {
    path: '/:id/valuation-type',
    params: paramByIDSchema,
    query: PaginationSchema,
    response: Type.Object({
      data: Type.Array(
        Type.Pick(DbSchema['product_valuation_type'], [
          'product_item_code',
          'valuation_type',
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },

  getManyStorageType: {
    path: '/storage-type',
    response: Type.Array(
      Type.Union([
        Type.Literal('frozen'),
        Type.Literal('dry'),
        Type.Literal('dry_ac'),
        Type.Literal('chilled'),
      ])
    ),
  },

  // ------------------------------------------------------
  // Product List from Highlight ( backoffice/product/list )
  // ------------------------------------------------------
  getManyProducts: {
    path: '/list',
    query: Type.Intersect([
      Type.Omit(SearchSchema, ['search_by']),
      PaginationSchema,
    ]),
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          Type.Pick(Product, ['id', 'title', 'item_code', 'brand_desc']),
          Type.Object({
            image: Type.Array(Type.String()),
            image_thumbnails: Type.Array(Type.String()),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },

  // ------------------------------------------------------
  // Reorder product of highlight
  // ------------------------------------------------------
  reorderProductHighlightItem: {
    path: '/highlight/:product_highlight_id/:item/:product_id/reorder',
    params: paramOrderProductHighlight,
    body: Type.Object({
      index: Type.Integer({ minimum: 1 }),
    }),
    response: Type.Object({
      message: Type.String(),
    }),
  },

  // ------------------------------------------------------
  // PLU Webhook
  // ------------------------------------------------------
  updatePLUWebhook: {
    path: '/plu',
    body: Type.Array(
      Type.Intersect([
        Type.Pick(DbSchema['webhook_plu'], [
          'price_id',
          'product_item_code',
          'distribution_channel',
          'cg_1_business_segmentation',
          'cg_4_manufacture',
          'cg_5_trading',
          'valuation_type',
          'price',
          'valid_from',
          'valid_until',
          'sap_updated_at',
        ]),
        Type.Object({
          cg_2_business_category: Type.Optional(Nullable(Type.String())),
          cg_3_business_type: Type.Optional(Nullable(Type.String())),
          uom: Type.Optional(Nullable(Type.String())),
          price_order: Type.Optional(Nullable(Type.Integer())),
        }),
      ])
    ),
    response: Type.Object({
      message: Type.String(),
    }),
  },

  // ------------------------------------------------------
  // PLK Webhook
  // ------------------------------------------------------
  updatePLKWebhook: {
    path: '/plk',
    body: Type.Array(
      Type.Pick(DbSchema['webhook_plk'], [
        'price_id',
        'product_item_code',
        'distribution_channel',
        'valuation_type',
        'price',
        'valid_from',
        'valid_until',
        'sap_no_shipto',
        'sap_updated_at',
      ])
    ),
    response: Type.Object({
      message: Type.String(),
    }),
  },

  // ------------------------------------------------------
  // Product Image Webhook
  // ------------------------------------------------------
  updateProductImageWebhook: {
    path: '/image',
    body: Type.Object({
      event_id: Type.String({ minLength: 1 }),
      items: Type.Array(
        Type.Object({
          item_code: Type.Integer(),
          filename: Type.String({ minLength: 1 }),
          s3_key: Type.String({ minLength: 1 }),
          uploaded_at: Type.Optional(Type.Integer({ minimum: 0 })),
        }),
        { minItems: 1 }
      ),
    }),
    response: Type.Object({
      message: Type.String(),
      accepted: Type.Integer({ minimum: 0 }),
      duplicated: Type.Integer({ minimum: 0 }),
    }),
  },
  deleteProductImageWebhook: {
    path: '/image/delete',
    body: Type.Object({
      event_id: Type.String({ minLength: 1 }),
      items: Type.Array(
        Type.Object({
          item_code: Type.Integer(),
          index: Type.Integer({ minimum: 1 }),
        }),
        { minItems: 1 }
      ),
    }),
    response: Type.Object({
      message: Type.String(),
      accepted: Type.Integer({ minimum: 0 }),
      duplicated: Type.Integer({ minimum: 0 }),
    }),
  },
};

export type ProductCrud = RecursiveStatic<typeof ProductCrudSchema>;
